import { useEffect, useState } from "react";
import { apiQuery } from "app/game-data/useQuery";
// import * as wordsLvl1 from "app/data/words-lvl-1.json";
// import * as wordsLvl2 from "app/data/words-lvl-2.json";
// import * as wordsLvl3 from "app/data/words-lvl-3.json";
// import * as wordsPlaytest from "app/data/playtest-words.json";
import useResetableStorage, {
  STORAGE_KEY_PREFIX,
} from "app/utils/useResetableStorage";

export const GAME_DATA_QUERY = /* GraphQL */ `
  query GAME_DATA($id: ID) {
    spellcheck {
      content(id: $id) {
        __typename
        id
        publishDate
        levels {
          __typename
          id
          category
          constructorName
          difficulty
          levelType
          name
          rowOrder
          videoEndTime
          videoStartTime
          videoWordTime
          videoUrl
          words {
            __typename
            definitionAudioUrl
            definitionText
            originAudioUrl
            originText
            rowOrder
            transcript
            wordAudioUrl
            wordText
          }
        }
      }
    }
  }
`;

export const RoundType = {
  SPELLING_BEE: "spelling_bee",
  BUZZ_IN: "buzz_in",
  SPEED: "speed",
  TED_WORD: "ted_word",
  ONE_CHANCE: "one_chance",
};

export type RoundType = (typeof RoundType)[keyof typeof RoundType];

export interface GameData {
  id: number;
  publishDate: string;
  token?: string;
  levels: Array<RoundData>;
}

export interface RoundData {
  id: number;
  constructorName: string;
  difficulty: string;
  levelType: RoundType;

  // name?: string; // NICK: Better hardcoded?
  rowOrder?: number; // Represents order of round within game

  // Only used for Speed rounds
  category?: string;

  // Only used for TED Word rounds
  videoUrl?: string;
  videoStartTime?: number;
  videoEndTime?: number;
  videoWordTime?: number;
  videoCaption?: string;

  words: WordData[];
}

export interface WordData {
  wordText: string;
  wordAudioUrl?: string;
  definitionAudioUrl?: string;
  definitionText?: string;
  originAudioUrl?: string;
  originText?: string;

  rowOrder?: number; // Order of word within level

  // Only used in TED Word round
  transcript?: string;
}

type GameDataQueryResponseType = {
  spellcheck: {
    content: GameData;
  };
};

export default function useFetchGameData(gameID?: number) {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useResetableStorage<GameData>(
    `${STORAGE_KEY_PREFIX}_GAME_DATA`,
  );

  // Check if we've already cached valid game data.
  const hasData = data?.id === gameID && isValidGameData(data);

  useEffect(() => {
    if (!gameID) {
      return;
    }

    if (hasData) {
      return;
    }

    async function fetchData(id: number) {
      try {
        setLoading(true);
        const { data, error } = await apiQuery<
          GameDataQueryResponseType,
          { id: number }
        >(GAME_DATA_QUERY, {
          id,
        });
        if (!error && data?.spellcheck.content?.id) {
          // Sort rounds & words by rowOrder
          data.spellcheck.content.levels.sort(
            (a, b) => (a.rowOrder ?? 0) - (b.rowOrder ?? 0),
          );
          data.spellcheck.content.levels.forEach((level) => {
            level.words.sort((a, b) => (a.rowOrder ?? 0) - (b.rowOrder ?? 0));
          });
          setData(data.spellcheck.content);
        } else {
          throw error;
        }
      } catch {
        console.log("Could not fetch data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData(gameID);
  }, [gameID, data?.id, setData, hasData]);

  return { loading, data };
}

// This is a bit hacky, but to force devices with a cached version of the
// older data structure to re-fetch today's data, we use this to confirm
// whether the data has the 'levels' property or not.
// Should not really be necessary after QA.
function isValidGameData(data?: GameData) {
  return data?.levels !== undefined;
}

// TODO: stuff below is only used for placeholder data, so delete after
// implementing fetching

// function getPlaceholderGameData(): GameData {
//   return {
//     id: 0,
//     publishDate: "11-11-2011",
//     token: "???",
//     levels: [
//       {
//         id: 0,
//         levelType: RoundType.SPELLING_BEE,
//         difficulty: "Easy",
//         words: [
//           getPlaytestWord("indigo"),
//           getPlaytestWord("duvet"),
//           getPlaytestWord("primatology"),
//           getPlaytestWord("citronella"),
//           getPlaytestWord("wunderkind"),
//         ],
//       },
//       {
//         id: 1,
//         levelType: RoundType.BUZZ_IN,
//         difficulty: "Medium",
//         words: [
//           getPlaytestWord("aerosol", true),
//           getPlaytestWord("bureaucrat", true),
//           getPlaytestWord("fuchsia", true),
//         ],
//       },
//       {
//         id: 2,
//         levelType: RoundType.SPEED,
//         difficulty: "Easy",
//         category: "Chess Terms",
//         words: [
//           getPlaytestWord("pawn"),
//           getPlaytestWord("sacrifice"),
//           getPlaytestWord("castle"),
//           getPlaytestWord("checkmate"),
//           getPlaytestWord("gambit"),
//           getPlaytestWord("adjourn"),
//           getPlaytestWord("desperado"),
//         ],
//       },
//       generateTedWordPlaceholder(3),
//       {
//         id: 2,
//         levelType: RoundType.ONE_CHANCE,
//         difficulty: "Hard",
//         words: [getPlaytestWord("adrenergic")],
//       },
//     ],
//   };
// }

// function generateTedWordPlaceholder(id: number): RoundData {
//   const baseUrl = import.meta.env.BASE_URL ?? "/";

//   const videoSlug = "tony_buffington_why_do_cats_act_so_weird";

//   return {
//     id: id,
//     levelType: RoundType.TED_WORD,
//     difficulty: "Easy",
//     videoUrl: videoSlug,
//     videoStartTime: 85,
//     videoEndTime: 97,
//     videoWordTime: 90,
//     words: [
//       {
//         wordText: "instinctually",
//         wordAudioUrl: `${baseUrl}audio/lvl_1/accuracy.mp3`,
//         definitionText: "n/a",
//         definitionAudioUrl: `${baseUrl}audio/lvl_1/accuracy_def.mp3`,
//         originText: "n/a",
//         originAudioUrl: `${baseUrl}audio/lvl_1/accuracy_origin.mp3`,
//         transcript:
//           "Grizmo doesn't need these particular skills to find and hunt down dinner in her food bowl today, but instinctually, viewing the living room from the top of her bookcase is exactly what she has evolved to do.",
//       },
//     ],
//   };
// }

// function getPlaytestWord(word: string, useLongDef = false): WordData {
//   const baseUrl = import.meta.env.BASE_URL ?? "/";
//   const wordDatas = wordsPlaytest as Record<
//     string,
//     { longDef: string; shortDef: string; origin: string }
//   >;

//   return {
//     wordText: word,
//     wordAudioUrl: `${baseUrl}audio/playtest/${word}.mp3`,
//     definitionText: useLongDef
//       ? wordDatas[word].longDef
//       : wordDatas[word].shortDef,
//     definitionAudioUrl: `${baseUrl}audio/playtest/${word}_${
//       useLongDef ? "longDef" : "shortDef"
//     }.mp3`,
//     originText: wordDatas[word].origin,
//     originAudioUrl: `${baseUrl}audio/playtest/${word}_origin.mp3`,
//   };
// }
